import {
  useShowInstructions,
  type LayoutModule,
} from '@backstage-components/base';
import {useSubscription} from 'observable-hooks';
import {PropsWithChildren, useEffect, useMemo, useRef, type FC} from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigate as useRouterNavigate,
  type NavigateFunction,
} from 'react-router';
import {reactName, RouterInstructionSchema} from './RouterDefinition';

export type RouterComponentDefinition = LayoutModule<
  typeof reactName,
  RouterContainerProps
>;

/**
 * Creates a `react-router` `Routes` node which also subscribes to `Router`
 * instructions in order to manage page navigation with the site `Instruction`
 * flows.
 */
export const RouterContainer: FC<PropsWithChildren<RouterContainerProps>> = (
  props
) => {
  const location = useLocation();
  const navigate = useRouterNavigate();
  const {observable, broadcast} = useShowInstructions(RouterInstructionSchema);
  // Use a ref rather than state so that the useSubscription function can only
  // be a closure over the correct value because the ref is mutated in place
  const navigateFn = useRef(props.navigate ?? navigate);
  useEffect(() => {
    navigateFn.current = props.navigate ?? navigate;
  }, [navigate, props.navigate]);

  useSubscription(observable, {
    next: (instruction) => {
      if (instruction.type === 'Router:goto') {
        navigateFn.current(`${props.prefix ?? ''}${instruction.meta.path}`);
      }
    },
  });

  const currentPath = useMemo(() => {
    const pathname = props.prefix
      ? location.pathname.replace(props.prefix, '')
      : location.pathname;
    return pathname === '' ? '/' : pathname;
  }, [location.pathname, props.prefix]);

  // The `UiRouter` in `@backstage/ui-render` always produces at least a
  // fallback route so the minimum number of routes (before data loads) is 1
  const routes = useMemo(
    () => createRoutesFromChildren(props.children),
    [props.children]
  );
  const hasRoutes = useMemo(() => routes.length > 1, [routes]);

  // broadcast a route change instruction when currentPath changes
  useEffect(() => {
    if (!hasRoutes) {
      return;
    }
    broadcast({
      type: 'Router:on-navigate',
      meta: {currentPath},
    });
    const timeout = setTimeout(() =>
      broadcast({
        type: 'Router:on-navigate-done',
        meta: {currentPath},
      })
    );
    return () => {
      clearTimeout(timeout);
    };
  }, [broadcast, currentPath, hasRoutes]);

  // broadcast on-404-no-match instruction if no routes were matched
  useEffect(() => {
    if (!hasRoutes) {
      return;
    }
    const matches = matchRoutes(routes, location);
    if (matches?.[0]?.route.path === '*') {
      broadcast({
        type: 'Router:on-404-no-match',
        meta: {currentPath},
      });
    }
  }, [broadcast, currentPath, hasRoutes, routes, location]);

  return <Routes>{props.children}</Routes>;
};

export interface RouterContainerProps {
  /**
   * Function called in order to navigate between pages, this function is used
   * in processing `Router:goto` instructions.
   * @default `useNavigate` from `react-router` package
   */
  navigate?: NavigateFunction;
  /**
   * If provided, `prefix` is prepended to every path before navigation is
   * triggered.
   */
  prefix?: string;
}
